/**
 * ============================================
 * MODULE: mp-dialog-swatches
 * PURPOSE: Injects cross-product color swatches into Horizon's quick-add-dialog.
 *
 * APPROACH:
 *   1. Capture-phase click listener records which product card (and QuickAddComponent
 *      instance) triggered the dialog.
 *   2. MutationObserver on the <dialog> `open` attribute fires when the dialog opens.
 *      Horizon's flow is: morph → updateQuickAddModal → showDialog (adds `open`),
 *      so by the time `open` appears, the modal content is already ready.
 *   3. The .mp-color-swatches__list UL is cloned (NOT the mp-cross-product-swatches-picker
 *      custom element) so that its connectedCallback never fires. This prevents the
 *      component from navigating to the product page when a swatch is clicked in
 *      the dialog.
 *   4. Swatch selection inside the dialog calls fetchProductPage + updateQuickAddModal
 *      on the stored QuickAddComponent instance, then re-injects swatches.
 *
 * ADDED: 2026-02-23 by MeshPlanet
 * ============================================
 */

/** @type {Element | null} Swatch picker from the product card that triggered the dialog */
let pendingSwatchPicker = null;

/** @type {any} QuickAddComponent DOM element / instance that triggered the dialog */
let pendingQuickAddComp = null;

/**
 * data-mp-product-url of the swatch currently active inside the dialog.
 * Null = use the card's committed selection.
 * @type {string | null}
 */
let activeDialogProductUrl = null;

/**
 * data-variant-id of the swatch currently active inside the dialog.
 * Used for variant-mode swatches where all inputs share the same product URL.
 * @type {string | null}
 */
let activeDialogVariantId = null;

// ─── Capture context when the quick-add button is clicked ────────────────────

document.addEventListener(
  'click',
  (event) => {
    const target = /** @type {Element} */ (event.target);
    const quickAddComp = target.closest('quick-add-component');
    if (!quickAddComp) return;

    // Reset per-dialog state
    pendingQuickAddComp = quickAddComp;
    activeDialogProductUrl = null;
    activeDialogVariantId = null;

    const card = quickAddComp.closest('product-card');
    pendingSwatchPicker = card?.querySelector('mp-cross-product-swatches-picker') ?? null;
  },
  true // capture phase — runs before Horizon's handlers
);

// ─── Observe dialog `open` attribute ─────────────────────────────────────────

/**
 * Observes the `open` attribute on the quick-add <dialog>. When the dialog
 * gains `open`, Horizon's morph has already completed, so modal content is ready.
 */
function initDialogObserver() {
  const dialogWrapper = document.getElementById('quick-add-dialog');
  if (!dialogWrapper) return;

  const dialogEl = dialogWrapper.querySelector('dialog');
  if (!dialogEl) return;

  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.attributeName !== 'open') continue;
      if (!dialogEl.hasAttribute('open')) continue; // dialog is closing, skip

      if (!pendingSwatchPicker) break; // product has no cross-product swatches

      const modalContent = document.getElementById('quick-add-modal-content');
      if (modalContent) {
        injectSwatches(modalContent, pendingSwatchPicker, activeDialogProductUrl);
      }
      break;
    }
  }).observe(dialogEl, { attributes: true, attributeFilter: ['open'] });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDialogObserver);
} else {
  initDialogObserver();
}

// ─── Inject swatches ──────────────────────────────────────────────────────────

/**
 * Clones .mp-color-swatches__list from the card's picker and injects it
 * before the variant-picker in the modal.
 *
 * We clone only the UL, not the mp-cross-product-swatches-picker element,
 * so the custom element's connectedCallback never fires and never navigates.
 *
 * @param {HTMLElement}  modalContent      - #quick-add-modal-content
 * @param {Element}      swatchPicker      - original picker from the product card
 * @param {string | null} activeProductUrl - URL of the currently active swatch
 *                                           (null = use card's committed selection)
 */
function injectSwatches(modalContent, swatchPicker, activeProductUrl) {
  // Remove any previous injection
  modalContent.querySelector('.mp-dialog-swatches')?.remove();

  // Clone the inner list only — skip the custom element wrapper
  const swatchList = swatchPicker.querySelector('.mp-color-swatches__list');
  if (!swatchList) return;

  const clone = /** @type {Element} */ (swatchList.cloneNode(true));

  // Remove hover-preview event bindings (on:pointerenter/leave) — irrelevant in dialog
  clone.querySelectorAll('[on\\:pointerenter], [on\\:pointerleave]').forEach((el) => {
    el.removeAttribute('on:pointerenter');
    el.removeAttribute('on:pointerleave');
  });

  // Show all swatches in dialog: remove card-only hiding class and +N badge
  clone.querySelectorAll('.mp-swatch--hidden').forEach((el) => el.classList.remove('mp-swatch--hidden'));
  clone.querySelector('.mp-color-swatches__count')?.remove();

  // Reflect the correct checked state
  clone.querySelectorAll('input[type="radio"]').forEach((r) => {
    /** @type {HTMLInputElement} */ (r).checked = false;
  });

  if (activeProductUrl) {
    // A swatch was already clicked inside this dialog session — keep that selection
    // Use variant ID for precise matching when swatches share the same product URL (variant mode)
    let activeSelector = `input[data-mp-product-url="${activeProductUrl}"]`;
    if (activeDialogVariantId) {
      activeSelector += `[data-variant-id="${activeDialogVariantId}"]`;
    }
    const active = /** @type {HTMLInputElement | null} */ (
      clone.querySelector(activeSelector)
    );
    if (active) active.checked = true;
  } else {
    // Mirror the card picker's current JS checked state (property, not attribute)
    // Use variant ID for precise matching in variant mode (all inputs share the same product ID)
    const checkedOriginal = /** @type {HTMLInputElement | null} */ (
      swatchPicker.querySelector('input[type="radio"]:checked')
    );
    if (checkedOriginal) {
      const productId = checkedOriginal.dataset.mpProductId;
      const variantId = checkedOriginal.dataset.variantId;
      let mirrorSelector = `input[data-mp-product-id="${productId}"]`;
      if (variantId) {
        mirrorSelector += `[data-variant-id="${variantId}"]`;
      }
      const toCheck = /** @type {HTMLInputElement | null} */ (
        clone.querySelector(mirrorSelector)
      );
      if (toCheck) toCheck.checked = true;
    }
  }

  // Build the "Color: Ash" label — read option name from data attribute set in Liquid
  const colorOptionName = /** @type {HTMLElement} */ (swatchPicker).dataset.colorLabel || 'Color';
  const checkedInput = /** @type {HTMLInputElement | null} */ (
    clone.querySelector('input[type="radio"]:checked')
  );
  const colorValueName = checkedInput?.getAttribute('aria-label') || '';

  const labelEl = document.createElement('p');
  labelEl.className = 'mp-dialog-swatches__label';
  labelEl.textContent = `${colorOptionName}: ${colorValueName}`;

  const wrapper = document.createElement('div');
  wrapper.className = 'mp-dialog-swatches';
  wrapper.appendChild(labelEl);
  wrapper.appendChild(clone);

  // Insert before variant-picker; fall back to prepending in modal content
  const variantPicker = modalContent.querySelector('variant-picker');
  const parent = variantPicker?.parentElement ?? modalContent;
  parent.insertBefore(wrapper, variantPicker);

  attachSwatchHandlers(wrapper, modalContent);
}

// ─── Swatch click inside dialog → reload dialog content ──────────────────────

/**
 * Attaches change listeners to the injected swatch radio inputs.
 * On selection: fetches the new product via the stored QuickAddComponent,
 * re-morphs the modal content, then re-injects swatches with the new selection.
 *
 * @param {HTMLElement} wrapper      - .mp-dialog-swatches container
 * @param {HTMLElement} modalContent - #quick-add-modal-content
 */
function attachSwatchHandlers(wrapper, modalContent) {
  wrapper.querySelectorAll('input[type="radio"]').forEach((input) => {
    if (!(input instanceof HTMLInputElement)) return;

    input.addEventListener('change', async () => {
      const productUrl = input.dataset.mpProductUrl;
      const variantId = input.dataset.variantId;
      if (!productUrl || !pendingQuickAddComp) return;

      activeDialogProductUrl = productUrl;
      activeDialogVariantId = variantId || null;

      // Build fetch URL — append variant param for variant-mode swatches
      let fetchUrl = productUrl;
      if (variantId) {
        const url = new URL(productUrl, window.location.origin);
        url.searchParams.set('variant', variantId);
        fetchUrl = url.pathname + url.search;
      }

      // Update the "Color: X" label immediately on selection
      const labelEl = wrapper.querySelector('.mp-dialog-swatches__label');
      if (labelEl) {
        const colorOptionName = /** @type {HTMLElement | null} */ (pendingSwatchPicker)?.dataset.colorLabel || 'Color';
        labelEl.textContent = `${colorOptionName}: ${input.getAttribute('aria-label') || ''}`;
      }

      // Dim variant picker while loading
      const variantPicker = /** @type {HTMLElement | null} */ (
        modalContent.querySelector('variant-picker')
      );
      if (variantPicker) variantPicker.style.opacity = '0.4';

      try {
        const html = await pendingQuickAddComp.fetchProductPage(fetchUrl);
        if (!html) return;

        const grid = html.querySelector('[data-product-grid-content]');
        if (!grid) return;

        // updateQuickAddModal runs morph then returns; re-inject swatches after
        await pendingQuickAddComp.updateQuickAddModal(
          /** @type {Element} */ (grid.cloneNode(true))
        );

        // morph removed .mp-dialog-swatches — inject again with updated selection
        if (pendingSwatchPicker) {
          injectSwatches(modalContent, pendingSwatchPicker, activeDialogProductUrl);
        }
      } finally {
        if (variantPicker) variantPicker.style.opacity = '';
      }
    });
  });
}
